import { z } from 'zod';

/**
 * Base monitoring configuration schema
 */
export const MonitoringConfigSchema = z.object({
  /** Service name for telemetry identification */
  serviceName: z.string().min(1),

  /** Deployment environment (e.g., production, staging, development) */
  environment: z
    .enum(['production', 'staging', 'development', 'test'])
    .default('development'),

  /** Service version/release identifier */
  serviceVersion: z.string().optional(),

  /** Enable/disable monitoring entirely */
  enabled: z.boolean().default(true),

  /** HyperDX API key for authentication */
  apiKey: z.string().optional(),

  /** OTLP endpoint for traces and metrics */
  otlpEndpoint: z.string().url().optional(),

  /** Sample rate for traces (0.0 to 1.0) */
  traceSampleRate: z.number().min(0).max(1).default(1.0),

  /** Enable console logging of telemetry */
  debug: z.boolean().default(false),
});

/**
 * Server-specific monitoring configuration
 */
export const ServerMonitoringConfigSchema = MonitoringConfigSchema.extend({
  /** Enable automatic instrumentation of HTTP, fetch, etc. */
  autoInstrumentation: z.boolean().default(true),

  /** Enable structured logging with trace correlation */
  enableLogging: z.boolean().default(true),

  /** Log level */
  logLevel: z.enum(['debug', 'info', 'warn', 'error']).default('info'),

  /** Enable metrics collection */
  enableMetrics: z.boolean().default(true),

  /** Custom attributes to add to all traces */
  customAttributes: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .optional(),
});

/**
 * Client-specific monitoring configuration
 */
export const ClientMonitoringConfigSchema = MonitoringConfigSchema.extend({
  /** Enable session replay */
  enableSessionReplay: z.boolean().default(true),

  /** Enable frontend error capture */
  enableErrorCapture: z.boolean().default(true),

  /** Privacy settings for session replay */
  privacy: z
    .object({
      /** Mask all text content */
      maskAllText: z.boolean().default(false),

      /** Mask all input values */
      maskAllInputs: z.boolean().default(true),

      /** Block media elements (images, videos) */
      blockMedia: z.boolean().default(false),

      /** CSS selectors to block from recording */
      blockSelectors: z.array(z.string()).default([]),

      /** CSS selectors to mask in recording */
      maskSelectors: z
        .array(z.string())
        .default(['.sensitive', '[data-private]']),
    })
    .default({}),

  /** User identification */
  user: z
    .object({
      id: z.string().optional(),
      email: z.string().email().optional(),
      name: z.string().optional(),
      customProperties: z.record(z.string(), z.any()).optional(),
    })
    .optional(),
});

export type MonitoringConfig = z.infer<typeof MonitoringConfigSchema>;
export type ServerMonitoringConfig = z.infer<
  typeof ServerMonitoringConfigSchema
>;
export type ClientMonitoringConfig = z.infer<
  typeof ClientMonitoringConfigSchema
>;

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Partial<MonitoringConfig> = {
  environment: 'development',
  enabled: true,
  traceSampleRate: 1.0,
  debug: false,
};

export const DEFAULT_SERVER_CONFIG: Partial<ServerMonitoringConfig> = {
  ...DEFAULT_CONFIG,
  autoInstrumentation: true,
  enableLogging: true,
  logLevel: 'info',
  enableMetrics: true,
};

export const DEFAULT_CLIENT_CONFIG: Partial<ClientMonitoringConfig> = {
  ...DEFAULT_CONFIG,
  enableSessionReplay: true,
  enableErrorCapture: true,
  privacy: {
    maskAllText: false,
    maskAllInputs: true,
    blockMedia: false,
    blockSelectors: [],
    maskSelectors: ['.sensitive', '[data-private]'],
  },
};
